import React, { useMemo } from 'react';

import { formatCurrency, formatPercent } from 'configs/columns';
import { useFeatureFlagVersion } from 'context/FeatureFlagContext';
import { Link, useNavigate } from 'react-router-dom';
import { getParticipationLabelFromRate } from 'features/pages/marketplace/ActiveTrades/shared/bidUtils';
import { Button } from 'ui-kit';

import { RequestAccessPublicListing } from './__generated__/RequestAccess.fragments';

import { GetUserInfo } from '../../query/__generated__/userData';

type GetUserInfo_user = NonNullable<GetUserInfo>['user'];
type RequestAccessProps = {
  listingId: string;
  listingDetails: RequestAccessPublicListing;
  dealId?: string | null;
  user?: GetUserInfo_user | undefined;
  isMyListing?: boolean;
};

const RequestAccess: React.FC<RequestAccessProps> = ({
  listingDetails,
  listingId,
  dealId,
  user,
  isMyListing = false,
}) => {
  const navigate = useNavigate();
  const bidsTableVersion = useFeatureFlagVersion('bidsTable');

  const listingCompanyId: string | null =
    'id' in listingDetails?.company ? listingDetails?.company?.id : null;

  const userCompanyId: string = user?.company?.id || '';

  const navigateToDeal = (id: string) => navigate(`/marketplace/deal/${encodeURIComponent(id)}`);

  const getActiveTradesUrl = () => {
    // If bidsTable feature flag is v1, use the tabbed interface with /all
    // Otherwise, use the base active_trades URL without tabs
    return bidsTableVersion === 'v1'
      ? '/marketplace/active_trades/all'
      : '/marketplace/active_trades';
  };

  const scaledServicingRate = listingDetails.servicing_fee_rate_bps ?? 0;

  const stats = useMemo(() => {
    const rows: { label: string; value: string }[] = [
      {
        label: 'UPB',
        value: formatCurrency(listingDetails.t_current_balance_cents),
      },
      { label: 'GWAC', value: formatPercent(listingDetails.wa_coupon_bps ?? 0, 3) },
      {
        label: 'NWAC',
        value: formatPercent((listingDetails.wa_coupon_bps ?? 0) - scaledServicingRate, 3),
      },
      {
        label: 'WAM',
        value: listingDetails.wa_remaining_loan_terms_months
          ? `${listingDetails.wa_remaining_loan_terms_months} Mo`
          : 'NO DATA',
      },
      {
        label: 'WALA',
        value: listingDetails.wa_loan_age_months
          ? `${listingDetails.wa_loan_age_months} Mo`
          : 'NO DATA',
      },
    ];
    rows.push({
      label: 'Ownership',
      value: getParticipationLabelFromRate(listingDetails.participation_rate),
    });
    return rows;
  }, [listingDetails, scaledServicingRate]);

  const renderOfferDetails = () => {
    return (
      <div className="mt-3 w-full max-w-[290px]">
        <h3 className="mb-3 text-sm uppercase text-slate-200">Offer Details</h3>
        {stats.map(({ label, value }) => (
          <div key={label} className="flex justify-between border-t border-slate-600 px-3 py-2">
            <span className="text-sm text-slate-200">{label}</span>
            <span className="text-sm text-slate-200">{value}</span>
          </div>
        ))}
      </div>
    );
  };

  // Request Access used to create a deal via CreateDeal (api#944, now removed); with no deal to
  // create, this only has something useful to do once a deal room already exists.
  const handleRequestBtnClick = () => {
    if (dealId) {
      navigateToDeal(dealId);
    }
  };

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="grid grid-cols-1 gap-6">
          <div className="flex min-h-0 flex-col">
            <div>
              <p className="mb-3 text-sm uppercase text-slate-200">
                {isMyListing ? 'Your Listing' : 'Pool Publish Settings'}
              </p>
              <p className="mb-2 text-sm text-slate-200">
                {isMyListing
                  ? "This is your company's listing. You can manage active trades and view deal activity for this pool of loans."
                  : (() => {
                      const isParticipation =
                        listingDetails.participation_rate != null &&
                        listingDetails.participation_rate < 100;
                      return (
                        <>
                          {isParticipation && (
                            <>
                              This is a <strong>participation</strong> offering (not a whole loan
                              sale).{' '}
                            </>
                          )}
                          Once the seller approves your request for data, you will be able to access
                          the loan tape, generate strats and run cashflows on this pool of loans,
                          and place a bid.
                        </>
                      );
                    })()}
              </p>
              <p className="text-sm text-slate-200">
                {isMyListing ? (
                  'Manage your deals and view buyer activity in the Active Trades section.'
                ) : (
                  <>
                    Track your deals in the{' '}
                    <Link
                      to={getActiveTradesUrl()}
                      className="uppercase text-purple-500 underline underline-offset-2"
                    >
                      Active Trades
                    </Link>{' '}
                    section.
                  </>
                )}
              </p>
            </div>
            <div className="mt-3 w-full max-w-[290px]">{renderOfferDetails()}</div>
          </div>
        </div>
      </div>
      {/* Fixed footer: sibling of the scroll region. The modal wrapper is a bounded
          flex column (overflow-hidden), so this row stays pinned at the bottom while
          the region above scrolls. */}
      <div className="mt-4 flex shrink-0 justify-end gap-2 border-t border-slate-600 bg-background-canvas pt-4">
        {isMyListing ? (
          <Button
            type="primary_green"
            size="small"
            label="View Trades"
            onClick={() => navigate(getActiveTradesUrl())}
          >
            View Active Trades
          </Button>
        ) : (
          userCompanyId !== listingCompanyId &&
          dealId && (
            <Button
              type="primary_green"
              size="small"
              label="Accept"
              onClick={() => handleRequestBtnClick()}
            >
              Go to Deal Room
            </Button>
          )
        )}
        <Button
          label="View"
          size="small"
          type="secondary_green"
          onClick={() => navigate(`/marketplace/listing/${listingId}`)}
        >
          View Stratifications
        </Button>
      </div>
    </div>
  );
};

export default RequestAccess;
